import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
  WheelEvent,
} from "react";
import Scrollbar from "react-scrollbars-custom";
import styled from "styled-components";
import { Track } from "./style/track";
import { Thumb } from "./style/thumb";
import { wheelConvert } from "utils";

const HiddenScrollbar = styled.div`
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

interface CustomScrollContainerProps {
  children: React.ReactNode;
  width: string;
  height: string;
  className?: string;
  maxHeight?: string;
  margin?: string;
  isHorizontalScroll?: boolean;
  onWheel?: (event: React.WheelEvent<HTMLDivElement>) => void;
}

export const CustomScrollContainer = forwardRef<
  Scrollbar,
  CustomScrollContainerProps
>(
  (
    {
      children,
      width,
      height,
      className,
      maxHeight,
      margin,
      isHorizontalScroll,
      onWheel,
    },
    scrollbarRef
  ) => {
    const hoverTimerId = useRef<NodeJS.Timeout>();
    const contentRef = useRef<HTMLDivElement | null>(null);
    const internalRef = useRef<Scrollbar | null>(null);
    const effectiveRef =
      (scrollbarRef as React.RefObject<Scrollbar>) || internalRef;

    const [contentHeight, setContentHeight] = useState(height);
    const [isHovered, setIsHovered] = useState(false);

    const onMouseEnter = () => {
      if (hoverTimerId.current) clearTimeout(hoverTimerId.current);
      setIsHovered(true);
    };
    const onMouseLeave = () => {
      if (hoverTimerId.current) clearTimeout(hoverTimerId.current);
      hoverTimerId.current = setTimeout(() => {
        setIsHovered(false);
      }, 1000);
    };

    const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
      onWheel?.(e);
      if (isHorizontalScroll) {
        wheelConvert(e, effectiveRef);
      }
    };

    useEffect(() => {
      if (contentRef.current && height === "max-content") {
        const observer = new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            const contentRect = entry.contentRect;
            setContentHeight(`${contentRect.height}px`);
          });
        });
        observer.observe(contentRef.current);

        return () => {
          observer.disconnect();
        };
      }
    }, [height]);

    return (
      <Scrollbar
        ref={effectiveRef as React.RefObject<Scrollbar & HTMLDivElement>}
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onWheel={handleWheel}
        style={{
          width,
          maxHeight,
          height: contentHeight,
          position: "relative",
          margin: margin,
          overflow: "auto",
        }}
        trackXProps={{
          renderer: (props) => {
            const { elementRef, key, ...restProps } = props;
            return <Track {...restProps} key={key} ref={elementRef} $xAxis />;
          },
        }}
        trackYProps={{
          renderer: (props) => {
            const { elementRef, key, ...restProps } = props;
            return <Track {...restProps} key={key} ref={elementRef} $yAxis />;
          },
        }}
        thumbXProps={{
          renderer: (props) => {
            const { elementRef, key, ...restProps } = props;
            return (
              <Thumb
                {...restProps}
                key={key}
                ref={elementRef}
                style={props.style}
                $visible={isHovered}
                $xAxis
              />
            );
          },
        }}
        thumbYProps={{
          renderer: (props) => {
            const { elementRef, key, ...restProps } = props;
            return (
              <Thumb
                {...restProps}
                key={key}
                ref={elementRef}
                style={props.style}
                $visible={isHovered}
                $yAxis
              />
            );
          },
        }}
        wrapperProps={{
          renderer: (props) => {
            const { elementRef, key, ...restProps } = props;
            return (
              <div
                {...restProps}
                key={key}
                ref={elementRef}
                style={{ overflow: "hidden" }}
              />
            );
          },
        }}
        contentProps={{
          renderer: (props) => {
            const { elementRef, key, ...restProps } = props;
            return (
              <div
                {...restProps}
                key={key}
                ref={(el) => {
                  elementRef && elementRef(el);
                  contentRef.current = el;
                }}
                style={{
                  ...props.style,
                  height: height === "max-content" ? height : "100%",
                  ...(height === "max-content" && {
                    minHeight: "unset",
                  }),
                }}
              >
                {children}
              </div>
            );
          },
        }}
        scrollerProps={{
          renderer: (props) => {
            const { elementRef, key, ...restProps } = props;
            return (
              <HiddenScrollbar
                {...restProps}
                key={key}
                ref={elementRef}
                style={{
                  ...props.style,
                  marginRight: 0,
                  marginBottom: 0,
                }}
              />
            );
          },
        }}
      />
    );
  }
);
